import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Header, Icon, Select, Modal, Popup, VerticalMenu } from '@scuf/common';
import { ItemInventoryPartDetailsDTO, SelectOption, Part, FilterInventoryDTO } from 'models/hsl/general';
import '../../../../styles/hsl.scss';
import { DataTable } from '@scuf/datatable';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';
import { HSL, COMPANY_MANAGER } from 'resources';
import { useLocation, Link, useLoaderData } from 'react-router-dom';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';

interface AddPart {
    setAddPart: (value: boolean) => void;
    onSubmit: () => void;
}

const SUPERADMIN = '1';
const ADMIN = '2';

const RepairAddPartsUsed: React.FC<AddPart> = ({ setAddPart, onSubmit }) => {
    const { t } = useTranslation(['common', 'hsl']);
    const { user: userDetails } = useUser();
    const location = useLocation();
    const selectedRepair = location.state?.selectedRepair;
    const inicialData = {
        Id: 0,
        ItemInventoryId: 0,
        CompanyId: selectedRepair?.CompanyId,
        CompanyName: selectedRepair?.CompanyName,
        CompanyCountry: '',
        ProductName: '',
        PartNumberDescription: '',
        PartNumber: '',
        FinalPrice: '',
        ListPrice: '',
        Available: 0,
        Quantity: 0,
        ResponsibleName: selectedRepair?.ResponsibleUser,
        ResponsibleId: userDetails?.ssoid,
        RepairId: selectedRepair?.Id,
        Pendent: false
    };
    const [data, setData] = useState<ItemInventoryPartDetailsDTO>(inicialData);
    const [usePnData, setUsePnData] = useState<ItemInventoryPartDetailsDTO>(inicialData);
    const [partPendent, setPartPendent] = useState(false);
    const [usePart, setUsePart] = useState(false);
    const [availableParts, setAvailableParts] = useState<SelectOption[]>([]);
    const isAdmin = userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN;


    const [company, getCompanyById, loadingCompany] = useFetch<{ id: number | any }, any>({
        url: COMPANY_MANAGER.getCompanyById,
        method: 'GET',
        onError: t('hsl:translation.NotCompany')
    })

    const [partsInventory, getAllPartsInventory] = useFetch<null, ItemInventoryPartDetailsDTO[]>({
        url: HSL.getInventoryItemsPartDetails,
        method: 'GET',
        onError: t('hsl:translation.NotFoundParts')
    });


    const [partsInventoryNew, getAllPartsInventoryNew, loadingInventoryNew] = useFetch<null, ItemInventoryPartDetailsDTO[]>({
        url: HSL.getParts,
        method: 'GET',
        onError: t('hsl:translation.NotFoundParts')
    });

    const [repairParts, getRepairParts, loadingRepairParts] = useFetch<{ repairId: number }, any>({
        url: HSL.getRepairPartsByRepairId,
        method: 'GET',
        onError: t('hsl:translation.Unexpected')
    });

    const [, addPnInRepair, loadingAddPn] = useFetch<ItemInventoryPartDetailsDTO, ItemInventoryPartDetailsDTO>({
        url: HSL.addPnInRepair,
        method: 'POST',
        onError: t('notification:critical.Add')
    });

    const [, removePnInRepair, loadingRemovePn] = useFetch<any, any>({
        url: HSL.removePnInRepair,
        method: 'POST',
        onError: t('notification:critical.Removed')
    });

    const [, usePnInRepair, loadingUsingPn] = useFetch<any, any>({
        url: HSL.usePnInRepair,
        method: 'POST',
        onError: t('notification:critical.Used')
    });

    useEffect(() => {
        getAllPartsInventory();
        getAllPartsInventoryNew();
        getCompanyById({ id: data.CompanyId })
        getRepairParts({ repairId: data.RepairId });
    }, []);

    useEffect(() => {
        const partsData = partsInventoryNew?.data;
        if (partsData) {
            const uniqueParts = Array.from(
                new Set(partsData.map((parts: ItemInventoryPartDetailsDTO) => String(parts.PartNumber)))
            );
            const mappedParts = uniqueParts.map(part => ({
                value: part ?? '',
                text: part ?? ''
            }));
            setAvailableParts(mappedParts);
        }
    }, [partsInventoryNew]);

    useEffect(() => {
        if (repairParts?.data) {
            const hasPendentParts = repairParts.data.some((part: { Pendent: boolean }) => part.Pendent === true);
            setAddPart(hasPendentParts);
        }
    }, [repairParts]);

    const AddPn = async () => {
        await addPnInRepair(data, () => {
            getAllPartsInventory();
            getRepairParts({ repairId: data.RepairId });
        });
        setData(inicialData);
    };

    const UsePnTest = async () => {
        await usePnInRepair(usePnData, () => {
            getAllPartsInventory();
            getRepairParts({ repairId: data.RepairId });
        });
    };

    const AddPnInventory = async () => {
        await addPnInRepair(data, () => {
            getAllPartsInventory();
            getRepairParts({ repairId: data.RepairId });
        });
    };

    const isTechOrClient = userDetails?.role === '3' || userDetails?.role === '4';

    const [, getRepairsbySerialNumber] = useFetch<{ Id: number }>({
        url: HSL.getRepairs,
        method: 'POST',
        onError: t('common:errors.Unexpected')
    });

    const [repairStatus, setRepairStatus] = useState<number | null>(null);

    useEffect(() => {
        const repairId = selectedRepair?.Id;
        if (repairId) {
            verificaStatusPorId(repairId).then(status => {
                setRepairStatus(status);
            });
        }
    }, [selectedRepair]);

    const partnerCompanyId = selectedRepair.CompanyId;

    const verificaStatusPorId = async (id: number): Promise<number> => {
        return new Promise(resolve => {
            getRepairsbySerialNumber({ Id: id }, resp => {
                const repairStatus = resp.data.Data?.[0]?.Status ?? -1;
                resolve(repairStatus);
            });
        });
    };

    const isDelivered = repairStatus === 4;
    const disabledFields = isTechOrClient && isDelivered;

    const RemovePn = async (rowData: ICellData) => {
        const rowDataJson = JSON.stringify(rowData);
        await removePnInRepair(rowDataJson, () => {
            getAllPartsInventory();
            getRepairParts({ repairId: data.RepairId });
        });
    };

    const handlePartChange = (selectedPartNumber: string) => {
        const selectedPart = partsInventory?.data?.find(part => part.PartNumber === selectedPartNumber && partnerCompanyId === part.CompanyId);
        const selectedPartOnParts = partsInventoryNew?.data?.find(part => part.PartNumber === selectedPartNumber);
        if (selectedPart || selectedPartOnParts) {
            setData({
                ...data,
                Id: selectedPart?.Id != null ? selectedPart?.Id : selectedPartOnParts?.Id,
                PartNumberDescription: selectedPart?.PartNumberDescription != null ? selectedPart?.PartNumberDescription : selectedPartOnParts?.Description,
                ProductName: selectedPart?.ProductName ?? "",
                CompanyCountry: company?.data.country,
                CompanyName: company?.data.name,
                FinalPrice: selectedPartOnParts?.FinalPrice?.replace(/[^0-9.,-]+/g, '')
                    .replace(',', '.')
                    .replace(/(\.\d*)(?=\.)/g, ''),
                Available: partnerCompanyId == selectedPart?.CompanyId ? selectedPart?.Quantity : 0,
                PartNumber: selectedPart?.PartNumber != null ? selectedPart?.PartNumber : selectedPartOnParts?.PartNumber,
                ItemInventoryId: selectedPart?.ItemInventoryId != null ? selectedPart.ItemInventoryId : data.ItemInventoryId,
            });
        }
    };

    const text = t('hsl:translation.AddPartPendent');
    const link = <Link to="/hsl/pending-items">{t('hsl:translation.Pendent')}</Link>;
    const secondLink = <Link to="/hsl/pending-items">{t('hsl:translation.SecondPendent')}</Link>;

    const renderedText = text
        .replace('{{link}}', '<<link>>')
        .replace('{{secondLink}}', '<<secondLink>>')
        .split(/(<<link>>|<<secondLink>>)/)
        .map((part, index) => {
            if (part === '<<link>>') return <React.Fragment key={index}>{link}</React.Fragment>;
            if (part === '<<secondLink>>') return <React.Fragment key={index}>{secondLink}</React.Fragment>;
            return <React.Fragment key={index}>{part}</React.Fragment>;
        });


    const dataParts = () => (
        <>
            <h1>
                <Icon root="common" name="entity-details" size="large" /> {t('hsl:translation.PartsUsed')}
            </h1>
            <div className="container-general-items">
                <Header title={t('hsl:translation.AddPartsUsed')} logo={false} menu={false} />

                <div className="form6Columns">
                    <Select
                        placeholder={t('hsl:translation.Part')}
                        label={t('hsl:translation.Part')}
                        value={data.PartNumber}
                        loading={loadingInventoryNew}
                        options={availableParts}
                        onChange={value => handlePartChange(value)}
                        search={true}
                        disabled={disabledFields || isAdmin}
                    />
                    <Input
                        className="description"
                        placeholder={t('hsl:translation.Description')}
                        value={data.PartNumberDescription}
                        onChange={value => setData({ ...data, PartNumberDescription: value })}
                        label={t('hsl:translation.Description')}
                        disabled={true}
                    />
                    <Input
                        placeholder={t('hsl:translation.Price')}
                        value={data.FinalPrice}
                        onChange={value => setData({ ...data, FinalPrice: value })}
                        label={t('hsl:translation.Price')}
                        disabled={true}
                    />
                    <Input
                        placeholder={t('hsl:translation.Available')}
                        value={String(data.Available)}
                        onChange={value => setData({ ...data, Available: Number(value) })}
                        label={t('hsl:translation.Available')}
                        disabled={true}
                    />
                    <Input
                        placeholder={t('hsl:translation.Quantity')}
                        type="number"
                        value={String(data.Quantity)}
                        onChange={value => setData({ ...data, Quantity: Number(value) })}
                        label={t('hsl:translation.Quantity')}
                        disabled={isAdmin}
                    />
                    <Button
                        className="button-add"
                        type="primary"
                        icon="slidercontrols-plus"
                        content={t('maintenancereport:translation.AddPN')}
                        iconPosition="right"
                        onClick={() => {
                            data.Available! < data.Quantity ? setPartPendent(true) : AddPn();
                        }}
                        loading={loadingCompany || loadingAddPn}
                        disabled={!data.PartNumber || data.Quantity <= 0}
                    />
                </div>
            </div>
        </>
    );

    const tablePartsRender = () => (
        <div className="Table-repair">
            <DataTable
                columnResizeMode="fit"
                data={repairParts?.data ?? []}
                loading={loadingRepairParts || loadingRemovePn || loadingUsingPn}
                resizableColumns={true}
            >
                <DataTable.Column field="Id" align="center" header={t('ID')} sortable />
                <DataTable.Column field="ProductName" align="center" header={t('common:translation.Model')} sortable />
                <DataTable.Column field="PartNumber" align="center" header={t('common:translation.PartNumber')} sortable />
                <DataTable.Column
                    field="PartNumberDescription"
                    align="center"
                    header={t('common:translation.Description')}
                    sortable
                />
                <DataTable.Column field="ResponsibleName" align="center" header={t('hsl:translation.User')} sortable />
                <DataTable.Column field="Quantity" align="center" header={t('hsl:translation.Quantity')} sortable />
                <DataTable.Column
                    field="Pendent"
                    align="center"
                    header={t('hsl:translation.Pendent')}
                    sortable
                    renderer={({ rowData }) => (
                        <span style={{ color: rowData.Pendent ? 'red' : 'black' }}>
                            {rowData.Pendent ? t('common:actions.Yes') : t('common:actions.No')}
                        </span>
                    )}
                />
                <DataTable.Column
                    field="Options"
                    align="center"
                    header={t('common:tabs.Options')}
                    renderer={e => (
                        <Popup
                            className="popup-theme-wrap"
                            element={<Icon size="large" root="global" name="settings" />}
                            on="click"
                            align="center">
                            <VerticalMenu>
                                <VerticalMenu.Header>{t('common:tabs.Options')}</VerticalMenu.Header>
                                <VerticalMenu.Item
                                    disabled={e.rowData.Pendent === false}
                                    onClick={() => {
                                        const updatedRowData2 = {
                                            ...e.rowData,
                                            CompanyId: company?.data.id
                                        }
                                        setUsePnData(updatedRowData2);
                                        setUsePart(true);
                                    }
                                    }
                                >
                                    <div className="Menu-vertical">
                                        <Icon
                                            name="UsePart"
                                            size="small"
                                            root="global"
                                            className="tools"
                                        />
                                        {t('hsl:translation.UsePart')}
                                    </div>
                                </VerticalMenu.Item>
                                <VerticalMenu.Item
                                    onClick={() => {
                                        const updatedRowData = {
                                            ...e.rowData,
                                            CompanyId: company?.data.id,
                                        }
                                        RemovePn(updatedRowData);
                                    }}
                                >
                                    <div className="Menu-vertical">
                                        <Icon
                                            name="DeleteParts"
                                            size="small"
                                            root="global"
                                            className="delete"
                                        />
                                        {t('hsl:translation.Remove')}
                                    </div>
                                </VerticalMenu.Item>
                            </VerticalMenu>
                        </Popup>)
                    }
                />
            </DataTable >
        </div >
    );

    const renderModalAddPartPendent = () => {
        return (
            <Modal
                open={partPendent}
                closeIcon={true}
                onClose={() => setPartPendent(false)}
                closeOnDimmerClick={true}
                size="large"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <img className="attention-icon" src={require('../../../../assets/attentionicon.png')} alt="Attention" />
                    <h1 className="attention-text">{t('hsl:translation.Attention')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <p className="attention-p">{renderedText}</p>
                </Modal.Content>

                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => {
                            const updatedData = data;
                            updatedData.Pendent = true;
                            setData(updatedData);
                            AddPnInventory();
                            setPartPendent(false);
                        }}
                    />
                    <Button type="primary" content={t('hsl:translation.Cancel')} onClick={() => setPartPendent(false)} />
                </Modal.Footer>
            </Modal>
        );
    };

    const renderModalUsePart = () => {
        return (
            <Modal
                open={usePart}
                closeIcon={true}
                onClose={() => setUsePart(false)}
                closeOnDimmerClick={true}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <img className="attention-icon-small" src={require('../../../../assets/attentionicon.png')} alt="Attention" />
                    <h2 className="attention-text2">{t('hsl:translation.Attention')}</h2>
                </Modal.Header>
                <Modal.Content align="center">
                    <br />
                    <div>
                        <p>{t('hsl:translation.UsePartText')}</p>
                    </div>
                    <br />
                    <div>
                        <em>{t('hsl:translation.UsePartTextComplement')}</em>
                    </div>
                    <br></br><br></br>
                    <div>
                        <Button
                            //type="secondary"
                            icon={"check"}
                            size='small'
                            content={t('hsl:translation.UsePart')}
                            onClick={() => {
                                UsePnTest();
                                setUsePart(false);
                            }}
                        />
                        <Button
                            type="primary"
                            icon={"badge-stop"}
                            size='small'
                            content={t('hsl:translation.Cancel')}
                            onClick={() => setUsePart(false)}
                        />
                    </div>
                </Modal.Content>
            </Modal>
        );
    };

    return (
        <>
            {dataParts()}
            {tablePartsRender()}
            {partPendent && renderModalAddPartPendent()}
            {usePart && renderModalUsePart()}
        </>
    );
};

export default RepairAddPartsUsed;

